import React from "react";
import Tooltip from "@mui/material/Tooltip";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";

import TopBarDisplayCreateTrainingContent from "../home/top_bar_components/TopBarDisplayCreateTrainingContent";
import SwipeDecksDisplay from "../create_training_content/SwipeDecksDisplay";
import SwipeDeckInputsModal from "../create_training_content/SwipeDeckInputsModal";
import FormsDisplay from "../create_training_content/FormsDisplay";
import FormEditorModal from "../create_training_content/FormEditorModal";
import { denormalizeEditorDeckForPreview } from "../create_training_content/swipeDeckHelpers";

// Lazy-load the swipe deck player so its bundle is only fetched when an admin
// opens a preview (mirrors CreateTrainingContentDisplay).
const SwipeDeckPlayerLazy = React.lazy(() =>
  import("../take_training_employee/swipe_deck_player/SwipeDeckPlayer"),
);

const ENGAGEMENT_PURPOSE = "engagement";

/**
 * Engagement tab: surveys, polls and swipe decks that are shared by public
 * link or QR code rather than assigned as training. Same LearningForm /
 * LearningSwipeDeck assets as Training Content, filtered by `purpose` and
 * edited through the same editors in their "engagement" variant (no roles,
 * scoring, grading, assignability or section embedding).
 */
function EngagementDisplay(props) {
  const [display, setDisplay] = React.useState("Surveys");

  // Derived the same way CreateTrainingContentDisplay does, but guarded: the
  // tab can mount before the store list has loaded.
  const homeCompany = React.useMemo(() => {
    const match = (props.storeNames || []).find((s) => s.id === props.homeStore);
    return match ? match.company : null;
  }, [props.storeNames, props.homeStore]);

  // Forms
  const [formsRefreshKey, setFormsRefreshKey] = React.useState(0);
  const [formModalOpen, setFormModalOpen] = React.useState(false);
  const [modalDataForm, setModalDataForm] = React.useState({});

  // Swipe decks
  const [swipeDeckRefreshKey, setSwipeDeckRefreshKey] = React.useState(0);
  const [swipeDeckModalOpen, setSwipeDeckModalOpen] = React.useState(false);
  const [modalDataDeck, setModalDataDeck] = React.useState(null);
  const [swipeDeckPreviewState, setSwipeDeckPreviewState] = React.useState(null);

  const railButton = (key, label, Icon, tooltip) => (
    <Tooltip title={tooltip} placement="right" arrow>
      <button
        className={`w-full py-3 px-4 text-left text-base flex items-center relative rounded-2xl ${
          display === key ? "bg-white" : ""
        }`}
        onClick={() => setDisplay(key)}
      >
        <div
          className={`flex flex-1 items-center space-x-4 ${
            display === key ? "text-violet-700" : "text-slate-500"
          }`}
        >
          <div>
            <Icon />
          </div>
          <span>{label}</span>
        </div>
      </button>
    </Tooltip>
  );

  if (!homeCompany) return null;

  return (
    <div className="w-full min-h-screen flex">
      <div className="w-1/5">
        <div className="h-full flex flex-col gap-1 px-6 py-12 bg-slate-50 border-r border-slate-200">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-4 pb-1">
            Engagement
          </div>
          {railButton(
            "Surveys",
            "Surveys",
            PollOutlinedIcon,
            "Surveys, polls and feedback forms. Share a link or QR code; responses roll up into a report.",
          )}
          {railButton(
            "SwipeDecks",
            "Swipe Decks",
            StyleOutlinedIcon,
            "Quick swipeable updates with optional poll cards. Share a link or QR code; nobody needs to log in.",
          )}
        </div>
      </div>

      <div className="w-4/5 min-h-screen">
        <div>
          <TopBarDisplayCreateTrainingContent
            title={"Engagement"}
            storeOptions={props.storeOptions}
            homeStore={props.homeStore}
            storeNames={props.storeNames}
            change_home_store={props.change_home_store}
          />
        </div>
        <div>
          {display === "Surveys" ? (
            <>
              <FormsDisplay
                key={formsRefreshKey}
                homeCompany={homeCompany}
                purpose={ENGAGEMENT_PURPOSE}
                onCreate={() => {
                  setModalDataForm({});
                  setFormModalOpen(true);
                }}
                onEdit={(form) => {
                  setModalDataForm({ uuid: form.uuid });
                  setFormModalOpen(true);
                }}
                onChanged={() => setFormsRefreshKey((k) => k + 1)}
              />
              <FormEditorModal
                open={formModalOpen}
                handleClose={() => setFormModalOpen(false)}
                modalDataForm={modalDataForm || {}}
                homeCompany={homeCompany}
                variant={ENGAGEMENT_PURPOSE}
                onSaveSuccess={() => setFormsRefreshKey((k) => k + 1)}
                onPublicShareChange={() => setFormsRefreshKey((k) => k + 1)}
              />
            </>
          ) : (
            <>
              <SwipeDecksDisplay
                key={swipeDeckRefreshKey}
                homeCompany={homeCompany}
                purpose={ENGAGEMENT_PURPOSE}
                onCreate={() => {
                  setModalDataDeck(null);
                  setSwipeDeckModalOpen(true);
                }}
                onEdit={(deck) => {
                  setModalDataDeck({ uuid: deck.uuid });
                  setSwipeDeckModalOpen(true);
                }}
                onPreview={(deck) => {
                  setSwipeDeckPreviewState({ deckUuid: deck.uuid });
                }}
                onChanged={() => setSwipeDeckRefreshKey((k) => k + 1)}
              />
              <SwipeDeckInputsModal
                open={swipeDeckModalOpen}
                onClose={() => setSwipeDeckModalOpen(false)}
                modalDataDeck={modalDataDeck}
                homeCompany={homeCompany}
                variant={ENGAGEMENT_PURPOSE}
                onSaved={() => setSwipeDeckRefreshKey((k) => k + 1)}
                onPublicShareChange={() => setSwipeDeckRefreshKey((k) => k + 1)}
                onPreview={(deckInEditor) => {
                  setSwipeDeckPreviewState({
                    previewDeck: denormalizeEditorDeckForPreview(deckInEditor),
                  });
                }}
              />
              {swipeDeckPreviewState && (
                <React.Suspense
                  fallback={
                    <div className="fixed inset-0 z-[1400] bg-black/95 flex items-center justify-center">
                      <div className="text-white/80 text-sm">Loading…</div>
                    </div>
                  }
                >
                  <SwipeDeckPlayerLazy
                    deckUuid={swipeDeckPreviewState.deckUuid}
                    previewDeck={swipeDeckPreviewState.previewDeck}
                    previewMode
                    companyId={homeCompany}
                    onClose={() => setSwipeDeckPreviewState(null)}
                  />
                </React.Suspense>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EngagementDisplay;
